import { useState, ReactNode } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Grid,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  Alert,
} from '@mui/material';
import {
  Person as PersonIcon,
  Timeline as TimelineIcon,
  Home as HomeIcon,
  Group as GroupIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';
import AdminStatusBadge from '../shared/AdminStatusBadge';
import UserActivityTimeline from './UserActivityTimeline';
import { AdminUserDetail } from '../../../types/admin';
import { format } from 'date-fns';

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-detail-tabpanel-${index}`}
      aria-labelledby={`user-detail-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

interface UserDetailTabsProps {
  user: AdminUserDetail;
  onBanUser: () => void;
  onUnbanUser: () => void;
  onDeleteUser: () => void;
}

/**
 * UserDetailTabs - Tabbed content for user detail drawer
 *
 * Tabs:
 * - Overview: User profile, stats, flags
 * - Activity: Activity timeline
 * - Homes: List of user's homes
 * - Relationships: Business, agents, clients
 * - Moderation: Ban/unban controls, admin notes
 */
export default function UserDetailTabs({
  user,
  onBanUser,
  onUnbanUser,
  onDeleteUser,
}: UserDetailTabsProps) {
  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (_event: unknown, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={currentTab} onChange={handleTabChange} aria-label="user detail tabs">
          <Tab icon={<PersonIcon />} label="Overview" iconPosition="start" />
          <Tab icon={<TimelineIcon />} label="Activity" iconPosition="start" />
          <Tab icon={<HomeIcon />} label={`Homes (${user.homes.length})`} iconPosition="start" />
          <Tab icon={<GroupIcon />} label="Relationships" iconPosition="start" />
          <Tab icon={<ShieldIcon />} label="Moderation" iconPosition="start" />
        </Tabs>
      </Box>

      {/* Overview Tab */}
      <TabPanel value={currentTab} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Profile Information
            </Typography>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  User ID
                </Typography>
                <Typography variant="body2">{user.uuid}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body2">{user.email}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Authentication
                </Typography>
                <Chip label={user.auth_type.toUpperCase()} size="small" />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Member Since
                </Typography>
                <Typography variant="body2">
                  {format(new Date(user.created_at), 'PPP')}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Account Status
            </Typography>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <AdminStatusBadge status={user.status} />
                </Box>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Subscription
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={user.subscription.tier.toUpperCase()} size="small" color="primary" />
                </Box>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Account Flags
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                  {user.is_business && <Chip label="Business Owner" size="small" color="secondary" />}
                  {user.is_agent && <Chip label="Agent" size="small" color="info" />}
                  {user.has_agent && <Chip label="Has Agent" size="small" />}
                  {user.is_admin && <Chip label="Admin" size="small" color="error" />}
                </Box>
              </Box>
              {user.last_login && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Last Login
                  </Typography>
                  <Typography variant="body2">
                    {format(new Date(user.last_login), 'PPpp')}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Statistics
            </Typography>
            <Paper sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="h4">{user.active_homes_count}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Active Homes
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="h4">{user.activity.length}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total Activities
                  </Typography>
                </Grid>
                {user.relationships.clients && (
                  <Grid item xs={6} sm={3}>
                    <Typography variant="h4">{user.relationships.clients.length}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Clients
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Activity Tab */}
      <TabPanel value={currentTab} index={1}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          User Activity History
        </Typography>
        <UserActivityTimeline activities={user.activity} />
      </TabPanel>

      {/* Homes Tab */}
      <TabPanel value={currentTab} index={2}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          User's Homes
        </Typography>
        {user.homes.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No homes created yet
            </Typography>
          </Paper>
        ) : (
          <List>
            {user.homes.map((home, index) => (
              <Box key={home.id}>
                <ListItem>
                  <ListItemText
                    primary={home.name}
                    secondary={
                      <Box>
                        {home.address && (
                          <Typography variant="caption" display="block">
                            {home.address}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          Created: {format(new Date(home.created_at), 'PP')}
                        </Typography>
                      </Box>
                    }
                  />
                  <AdminStatusBadge status={home.status} />
                </ListItem>
                {index < user.homes.length - 1 && <Divider />}
              </Box>
            ))}
          </List>
        )}
      </TabPanel>

      {/* Relationships Tab */}
      <TabPanel value={currentTab} index={3}>
        {user.relationships.business && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Business Affiliation
            </Typography>
            <Paper sx={{ p: 2 }}>
              <Typography variant="body1" fontWeight={500}>
                {user.relationships.business.name}
              </Typography>
              <Chip label={user.relationships.business.role} size="small" sx={{ mt: 1 }} />
            </Paper>
          </Box>
        )}

        {user.relationships.agent && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Agent
            </Typography>
            <Paper sx={{ p: 2 }}>
              <Typography variant="body1" fontWeight={500}>
                {user.relationships.agent.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user.relationships.agent.email}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Business: {user.relationships.agent.business}
              </Typography>
            </Paper>
          </Box>
        )}

        {user.relationships.clients && user.relationships.clients.length > 0 && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Clients ({user.relationships.clients.length})
            </Typography>
            <List>
              {user.relationships.clients.slice(0, 10).map((client, index) => (
                <Box key={index}>
                  <ListItem>
                    <ListItemText primary={client.name} secondary={client.email} />
                  </ListItem>
                  {index < Math.min(user.relationships.clients!.length, 10) - 1 && <Divider />}
                </Box>
              ))}
            </List>
            {user.relationships.clients.length > 10 && (
              <Typography variant="caption" color="text.secondary" sx={{ px: 2 }}>
                And {user.relationships.clients.length - 10} more clients...
              </Typography>
            )}
          </Box>
        )}

        {!user.relationships.business && !user.relationships.agent && !user.relationships.clients && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No relationships established
            </Typography>
          </Paper>
        )}
      </TabPanel>

      {/* Moderation Tab */}
      <TabPanel value={currentTab} index={4}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Moderation Actions
        </Typography>

        {user.status === 'banned' && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="bold">
              This user is currently banned
            </Typography>
            {user.ban_reason && (
              <Typography variant="body2">Reason: {user.ban_reason}</Typography>
            )}
            {user.banned_at && (
              <Typography variant="caption" color="text.secondary">
                Banned: {format(new Date(user.banned_at), 'PPpp')}
              </Typography>
            )}
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            User Actions
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
            {user.status === 'active' ? (
              <Button variant="outlined" color="error" onClick={onBanUser}>
                Ban User
              </Button>
            ) : (
              <Button variant="outlined" color="success" onClick={onUnbanUser}>
                Unban User
              </Button>
            )}
            <Button variant="outlined" color="error" onClick={onDeleteUser}>
              Delete User (Permanent)
            </Button>
          </Box>
        </Paper>

        {user.admin_notes && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Admin Notes
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user.admin_notes}
            </Typography>
          </Paper>
        )}
      </TabPanel>
    </Box>
  );
}
